/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { orderApi, Order } from '../../services/orderApi';

interface OrderState {
  orders: Order[];
  currentOrder: Order | null;
  total: number;
  page: number;
  loading: boolean;
  error: string | null;
}

const initialState: OrderState = {
  orders: [],
  currentOrder: null,
  total: 0,
  page: 1,
  loading: false,
  error: null,
};

// Async thunks
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (
    params: { page?: number; page_size?: number; status?: string } | undefined,
    { rejectWithValue }
  ) => {
    try {
      return await orderApi.getOrders(params);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch orders'
      );
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (id: number, { rejectWithValue }) => {
    try {
      return await orderApi.getOrderById(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch order'
      );
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  async (id: number, { rejectWithValue }) => {
    try {
      return await orderApi.cancelOrder(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to cancel order'
      );
    }
  }
);

// Slice
const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch orders
    builder.addCase(fetchOrders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchOrders.fulfilled, (state, action) => {
      state.loading = false;
      state.orders = action.payload.items;
      state.total = action.payload.total;
      state.page = action.payload.page;
    });
    builder.addCase(fetchOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch order by ID
    builder.addCase(fetchOrderById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchOrderById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentOrder = action.payload;
    });
    builder.addCase(fetchOrderById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Cancel order
    builder.addCase(cancelOrder.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(cancelOrder.fulfilled, (state, action) => {
      state.loading = false;
      // Update the order in the list
      const idx = state.orders.findIndex((o) => o.id === action.payload.order_id);
      if (idx !== -1) {
        state.orders[idx].status = 'cancelled';
      }
      if (state.currentOrder && state.currentOrder.id === action.payload.order_id) {
        state.currentOrder.status = 'cancelled';
      }
    });
    builder.addCase(cancelOrder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, clearCurrentOrder } = orderSlice.actions;
export default orderSlice.reducer;
